// Crew & Equipment — Week Totals on the Overview VoltaGrid tab: labor by
// classification, labor paid rate vs the rate sheet's target base wage
// (job to date), and equipment by type, styled after the
// Field Ticket Week Summary's "EQUIPMENT — WEEK TOTALS" table. Nothing here
// reads HeavyJob (Seth 10/29: "just want to make sure nothing is loading from
// heavy jobs"): every hour is JDE's or a reviewed time card's.
//
// Labor is JDE's actual cost alone (51100/51200 rows with a Staff_ID), for
// one week: the Monday–Sunday week before the Thursday the version was cut,
// grouped by JDE's own Labor_Classification_Description (Seth 10/29). The two
// tiles above it are that week and the one before.
//
// Equipment is the reviewed weekly time card workbook's Equipment sheet
// (02.06 - Timecards / Timecards_<job>_WE<m.d.yy>.xlsx, readApprovedTimeCard)
// for the version's week, or the newest card before it when project controls
// filed none that week (Seth 10/29), rolled up by rate sheet item.
//
// The paid-rate table is job to date off actual cost: each classification's
// 51100 straight-time cost ÷ hours is the actual base wage paid, set against
// the rate sheet's target base wage for the class it bills as (the Billing
// tab's Rates & Rules map) and, through the 1.633 multiplier, the
// straight-time billable rate each produces.

import React, { useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import { pNum, loadApprovedTimeCard, loadJobTimecards } from '../db/company';
import { selectedJobAtom, jobCostDetailAtom } from '../store/company';
import { stRate } from './billingRates';
import { machineName } from './billingEquipNames';
import { equipRowAs } from './billingContract';
import { parseEquipSheet } from './billingTicketDocs';
import { TAP_API_BASE } from './tapInvoiceViewer';

const ymd = (v) => {
    if (!v) return '';
    const s = String(v);
    const m = /^\d{4}-\d{2}-\d{2}/.test(s) ? moment.utc(s.slice(0, 10), 'YYYY-MM-DD') : moment.utc(s);
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
};
const normName = (s) => String(s || '').toLowerCase().replace(/&amp;/g, '&').replace(/[^a-z0-9]+/g, ' ').trim();
const typeKey = (desc) => String(desc || '').trim().replace(/\s+/g, ' ').toLowerCase() || 'unclassified';
// The week summary names a rental by its rate-sheet description and drops
// the JDE " - Bare Rate" / " - Bare Rental" tail; the client sees the
// machine, not the deal.
const cleanDesc = (desc) => String(desc || '').replace(/\s*-\s*bare (rate|rental)\s*$/i, '').trim();

/**
 * The labor weeks for a version: the Monday–Sunday week before the Thursday
 * the version was cut (the Thursday on or before its start date), and the
 * week before that, oldest first: [{ start, end }, { start, end }].
 * A version cut Thursday 10/22/26 reports 10/12 – 10/18 (Seth 10/29).
 */
// The Monday–Sunday week holding `day`, and the week before it, oldest first.
const weekPair = (day) => {
    const mon = moment.utc(day, 'YYYY-MM-DD').isoWeekday(1);
    return [7, 0].map((n) => ({
        start: mon.clone().subtract(n, 'days').format('YYYY-MM-DD'),
        end: mon.clone().subtract(n - 6, 'days').format('YYYY-MM-DD'),
    }));
};
export const versionWeeks = (versionStart) => {
    const d = ymd(versionStart) || moment().format('YYYY-MM-DD');
    const m = moment.utc(d, 'YYYY-MM-DD');
    const thu = m.isoWeekday() >= 4 ? m.clone().isoWeekday(4) : m.clone().subtract(7, 'days').isoWeekday(4);
    return weekPair(thu.clone().subtract(7, 'days').format('YYYY-MM-DD'));
};

/**
 * The weeks the labor table shows: the version's (versionWeeks) — unless JDE
 * has not posted labor into it yet (payroll posts a week or more behind), then
 * the newest week JDE has (Seth 10/29: "it should be a table of last week").
 * `through` is the last day JDE has labor. Returns { weeks, behind }.
 */
export const laborWeeksFor = (weeks, through) => {
    const target = weeks[weeks.length - 1];
    if (!through || through >= target.start) return { weeks, behind: false };
    return { weeks: weekPair(through), behind: true };
};

/**
 * Distinct people and hours in each { start, end } week, off labor day ×
 * person ({ d: { [staffId]: { cls, hours } } }), on the classes classOf
 * keeps (every one without it). Returns [{ start, end, people, hours }].
 */
export const weekTotals = (laborDays, weeks, classOf = null) => weeks.map(({ start, end }) => {
    const ids = new Set();
    let hours = 0;
    Object.keys(laborDays || {}).forEach((d) => {
        if (d < start || d > end) return;
        Object.keys(laborDays[d]).forEach((id) => {
            const r = laborDays[d][id];
            if (classOf && !classOf(id, r.cls)) return;
            ids.add(id);
            hours += r.hours;
        });
    });
    return { start, end, people: ids.size, hours };
});

/**
 * One week's JDE labor by classification: laborDays is { d: { [staffId]:
 * { cls, hours } } }, week { start, end }. The classification is JDE's
 * Labor_Classification_Description as it posted. Returns { dates, classes:
 * [{ key, label, people, byDate, total }], people }.
 */
export const laborWeek = (laborDays, week) => {
    const dates = [];
    for (let m = moment.utc(week.start, 'YYYY-MM-DD'); m.format('YYYY-MM-DD') <= week.end; m.add(1, 'day')) dates.push(m.format('YYYY-MM-DD'));
    const classes = {};
    const clsPeople = {};
    const everyone = new Set();
    dates.forEach((d) => {
        Object.keys((laborDays || {})[d] || {}).forEach((id) => {
            const r = laborDays[d][id];
            const label = r.cls || 'Unclassified';
            const k = typeKey(label);
            const c = classes[k] || (classes[k] = { key: k, label, people: 0, byDate: {}, total: 0 });
            c.byDate[d] = (c.byDate[d] || 0) + r.hours;
            c.total += r.hours;
            (clsPeople[k] || (clsPeople[k] = new Set())).add(id);
            everyone.add(id);
        });
    });
    Object.keys(classes).forEach((k) => { classes[k].people = clsPeople[k].size; });
    return {
        dates,
        // Distinct people: one who changes classification inside the week counts once.
        people: everyone.size,
        classes: Object.values(classes).sort((a, b) => b.total - a.total || a.label.localeCompare(b.label)),
    };
};

/**
 * The job's labor for the version's week, off JDE.
 * Returns { loading, labor: { dates, classes, people, weeks, through, behind } }:
 * laborWeek for the week shown; weeks = [week before, week shown] head count
 * and hours; through = the last day JDE has labor; behind = the version's week
 * when JDE has not posted it and an earlier week is shown instead.
 */
export function useJobLabor({ enabled = true, versionStart = '' } = {}) {
    const [jobCostDetail] = useAtom(jobCostDetailAtom);

    // Person × day ST+OT hours off 51100/51200, with JDE's classification.
    const laborByDate = useMemo(() => {
        const out = {};
        (jobCostDetail || []).forEach((r) => {
            const ct = String(r?.Cost_Type_Code || '');
            if (ct !== '51100' && ct !== '51200') return;
            const h = pNum(r.Total_Hours);
            const id = String(r.Staff_ID || '').trim();
            const d = ymd(r.Service_Date);
            if (!h || !id || !d) return;
            const day = out[d] || (out[d] = {});
            const p = day[id] || (day[id] = { id, cls: String(r.Labor_Classification_Description || '').trim(), hours: 0 });
            if (!p.cls && r.Labor_Classification_Description) p.cls = String(r.Labor_Classification_Description).trim();
            p.hours += h;
        });
        return out;
    }, [jobCostDetail]);
    const costReady = Array.isArray(jobCostDetail);
    const weeks = useMemo(() => versionWeeks(versionStart), [versionStart]);

    return useMemo(() => {
        const noLabor = { dates: [], classes: [], people: 0, weeks: [], through: '', behind: null };
        if (!enabled) return { loading: false, labor: noLabor };
        const through = Object.keys(laborByDate).sort().pop() || '';
        const shown = laborWeeksFor(weeks, through);
        const labor = {
            ...laborWeek(laborByDate, shown.weeks[shown.weeks.length - 1]),
            weeks: weekTotals(laborByDate, shown.weeks), through,
            behind: shown.behind ? weeks[weeks.length - 1] : null,
        };
        return { loading: !costReady, labor };
    }, [enabled, costReady, laborByDate, weeks]);
}

/**
 * A reviewed time card's Equipment sheet (readApprovedTimeCard: [{ desc,
 * unit, type, days: { d: h }, total }]) rolled up by rate sheet item for the
 * week ending `we`. nameUnit(unit, desc) names the item ({ name }, or a falsy
 * name to keep the sheet's description). Returns { dates, units, types }.
 */
export const cardEquipment = (equipment, we, nameUnit = null) => {
    const dates = Array.from({ length: 7 }, (_, i) => moment.utc(we, 'YYYY-MM-DD').subtract(6 - i, 'days').format('YYYY-MM-DD'));
    const units = [];
    const types = {};
    (equipment || []).forEach((e) => {
        const byDate = {};
        let total = 0;
        dates.forEach((d) => { const h = Number((e.days || {})[d]) || 0; if (h) { byDate[d] = h; total += h; } });
        if (!total) return;
        const desc = cleanDesc(e.desc);
        const unit = String(e.unit || '').trim();
        units.push({ unit, desc, total });
        const named = nameUnit ? nameUnit(unit, desc) : null;
        const label = (named && named.name) || desc;
        const k = typeKey(label);
        const t = types[k] || (types[k] = { key: k, label: label || 'Unclassified', units: 0, byDate: {}, total: 0 });
        t.units += 1; t.total += total;
        Object.keys(byDate).forEach((d) => { t.byDate[d] = (t.byDate[d] || 0) + byDate[d]; });
    });
    return { dates, units, types: Object.values(types).sort((a, b) => b.total - a.total || a.label.localeCompare(b.label)) };
};

// The time card is looked for in the version's week, then up to this many
// weeks back (project controls does not file every week's card).
const CARD_LOOKBACK = 4;

// A week's signed field ticket summary workbook in the same folder —
// "Field_Ticket_54026071_WE10.18.26_Week_Summary.xlsx", the Labor + Equipment
// workbook the client signed — for a week project controls filed no
// Timecards_ workbook (71 WE 10/18).
const summaryName = (job, we) => {
    const m = moment.utc(we, 'YYYY-MM-DD');
    return `field_ticket_${job}_we${m.month() + 1}.${m.date()}.${m.format('YY')}_week_summary.xlsx`.toLowerCase();
};
const readWeekSummary = async (files, job, we) => {
    const want = summaryName(job, we);
    const f = (files || []).filter((x) => String(x.name || '').toLowerCase() === want)
        .sort((a, b) => String(b.lastModifiedDateTime || b.lastModified || '').localeCompare(String(a.lastModifiedDateTime || a.lastModified || '')))[0];
    if (!f || !f.id || !f.driveId) return null;
    const res = await fetch(`${TAP_API_BASE}api/jobTimecardFile?driveId=${encodeURIComponent(f.driveId)}&id=${encodeURIComponent(f.id)}`);
    if (!res.ok) throw new Error(`${f.name}: the file proxy answered ${res.status}`);
    const mod = await import('xlsx');
    const XLSX = mod.default || mod;
    const wb = XLSX.read(new Uint8Array(await res.arrayBuffer()), { type: 'array' });
    const tab = wb.SheetNames.find((x) => /equipment/i.test(x));
    if (!tab) return null;
    const grid = XLSX.utils.sheet_to_json(wb.Sheets[tab], { header: 1, raw: true, defval: null });
    return { file: { name: f.name, webUrl: f.webUrl || null }, equipment: parseEquipSheet(grid, we) };
};

/**
 * The reviewed time card for the version's week from SharePoint (02.06 -
 * Timecards / Timecards_<job>_WE<m.d.yy>.xlsx), else that week's signed
 * week summary workbook, else the same for the weeks before it.
 * Returns { loading, error, we, file, equipment }.
 */
function useWeekCard(job, weekEnd) {
    const [card, setCard] = useState({ key: '', loading: false, error: null, we: '', file: null, equipment: [] });
    const key = `${job}|${weekEnd}`;
    useEffect(() => {
        if (!job || !weekEnd) return undefined;
        let dead = false;
        setCard({ key, loading: true, error: null, we: '', file: null, equipment: [] });
        (async () => {
            let files = null;
            for (let i = 0; i <= CARD_LOOKBACK; i += 1) {
                const we = moment.utc(weekEnd, 'YYYY-MM-DD').subtract(7 * i, 'days').format('YYYY-MM-DD');
                const res = await loadApprovedTimeCard({ job: String(job), weekEnd: we });
                if (dead) return;
                if (res && res.found && (res.equipment || []).length) {
                    setCard({ key, loading: false, error: null, we, file: res.file || null, equipment: res.equipment });
                    return;
                }
                if (!files) files = ((await loadJobTimecards({ job: String(job) })) || {}).files || [];
                if (dead) return;
                const sum = await readWeekSummary(files, String(job), we);
                if (dead) return;
                if (sum && sum.equipment.length) {
                    setCard({ key, loading: false, error: null, we, file: sum.file, equipment: sum.equipment });
                    return;
                }
            }
            setCard({ key, loading: false, error: null, we: '', file: null, equipment: [] });
        })().catch((e) => { if (!dead) setCard({ key, loading: false, error: String(e?.message || e), we: '', file: null, equipment: [] }); });
        return () => { dead = true; };
    }, [job, weekEnd, key]);
    return card.key === key ? card : { key, loading: true, error: null, we: '', file: null, equipment: [] };
}

/**
 * Job-to-date straight-time paid rate by classification, against the
 * rate sheet's target base wage. `billsAs`, `personCls`, `sheetLabor` and
 * `rateCfg` come from useBillingRevenue (the Billing tab's class map, the
 * Force Report's per-person map, the sheet index and wage multiplier).
 * A line resolves the way pricing does — saved class mapping, then the
 * person's Force Report class, then the class-wide map — and rows are one
 * per sheet class, so every JDE classification that bills as "Craft Helper
 * - A" folds into one row (Seth 10/9). The unmapped lines share one row.
 * `jdes` lists the JDE classifications behind a row, most hours first.
 * Returns [{ key, label, jdes, sheet, people, hours, cost, paid, target, delta, pct, billPaid, billTarget }].
 */
// Craft only: the rate analysis is about the trades' wage vs the sheet, so
// management, office and other indirect classifications (construction
// manager and up, project controls, superintendents, HSE / safety) stay out
// of it. Foremen and general foremen are craft and stay in (Steve 10/8).
const NON_CRAFT_RE = /\b(manager|director|executive|president|controls|buyer|expeditor|estimator|engineer|coordinator|scheduler|administrator|admin|clerk|accountant|analyst|superintendent|super|hse|safety|specialist|qc|qa|quality|inspector)\b/i;
export const isCraftClass = (cls) => !NON_CRAFT_RE.test(String(cls || ''));

/**
 * The rate sheet (service order) class a labor line bills as, resolved the
 * way pricing resolves it: a saved class mapping, then the person's Force
 * Report class, then the class-wide map. Null: the classification is not on
 * the service order (an HR business partner charged to the job).
 */
export function sheetClassOf(cls, id, billsAs, personCls, sheetLabor) {
    const byName = (sheetLabor && sheetLabor.byName) || {};
    const cm = (billsAs || {})[cls];
    const pcName = cm?.saved ? '' : ((personCls || {})[id] || '');
    return (pcName && byName[pcName]) || cm || null;
}

export function laborRatesOf(jobCostDetail, billsAs, rateCfg, personCls, sheetLabor) {
    const cfg = rateCfg || { m: 1.633, otf: 1.4 };
    const by = {};
    (jobCostDetail || []).forEach((r) => {
        if (String(r?.Cost_Type_Code || '') !== '51100') return;
        const h = pNum(r.Total_Hours);
        const cost = pNum(r.Job_Cost);
        if (!h && !cost) return;
        const cls = String(r.Labor_Classification_Description || '').trim();
        if (!isCraftClass(cls)) return;
        const id = String(r.Staff_ID || '').trim();
        const m = sheetClassOf(cls, id, billsAs, personCls, sheetLabor);
        if (m && !isCraftClass(m.name)) return;
        const k = m ? typeKey(m.name) : '|unmapped';
        const c = by[k] || (by[k] = { key: k, m, jdeHours: {}, people: new Set(), hours: 0, cost: 0 });
        const j = cls || 'Unclassified';
        c.jdeHours[j] = (c.jdeHours[j] || 0) + h;
        c.hours += h; c.cost += cost;
        if (id) c.people.add(id);
    });
    return Object.values(by).map((c) => {
        const m = c.m;
        const jdes = Object.keys(c.jdeHours).sort((a, b) => c.jdeHours[b] - c.jdeHours[a] || a.localeCompare(b));
        const paid = c.hours > 0 ? c.cost / c.hours : 0;
        const target = m && m.wage > 0 ? m.wage : 0;
        const delta = paid && target ? paid - target : null;
        return {
            key: c.key, label: jdes.join(', '), jdes, sheet: m ? m.name : '', people: c.people.size, hours: c.hours, cost: c.cost,
            paid, target, delta, pct: delta != null ? delta / target : null,
            billPaid: paid ? stRate(paid, cfg) : 0, billTarget: m ? m.st : 0,
        };
    // Sheet class A→Z, the unmapped row at the bottom.
    }).sort((a, b) => (a.sheet ? 0 : 1) - (b.sheet ? 0 : 1) || a.sheet.localeCompare(b.sheet));
}

const fmtD = (d) => (d ? moment(d, 'YYYY-MM-DD').format('M/D/YY') : '');
const fmtR = (v) => (v || v === 0 ? Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' }) : '');
const fmtS = (v) => (v == null ? '' : `${v > 0 ? '+' : ''}${Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}`);
const fmtP = (v) => (v == null ? '' : `${v > 0 ? '+' : ''}${(v * 100).toFixed(1)}%`);
const fmtH = (h) => (h ? Number(h).toLocaleString(undefined, { maximumFractionDigits: 1 }) : '');

// The week summary's table: ink header, hairline rows, bold TOTAL column,
// "Total Hours" foot. `lead` is the one or two text columns on the left.
function WeekTable({ palette, lead, dates, rows, foot }) {
    const INK = '#1e293b';
    const th = { background: INK, color: '#fff', fontSize: 7.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', padding: '6px 8px', textAlign: 'center', whiteSpace: 'nowrap' };
    const thL = { ...th, textAlign: 'left' };
    const td = { fontSize: 11, color: palette.text, padding: '5px 8px', borderBottom: `1px solid ${palette.border}`, textAlign: 'center', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
    const tdL = { ...td, textAlign: 'left', whiteSpace: 'normal' };
    const ft = { ...td, fontWeight: 700, borderTop: `1px solid ${INK}`, borderBottom: 'none', background: '#f8fafc' };
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                    <tr>
                        {lead.map((c) => <th key={c.key} style={c.center ? th : thL}>{c.label}</th>)}
                        {dates.map((d) => <th key={d} style={th}>{fmtD(d)}</th>)}
                        <th style={th}>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.key} style={{ background: i % 2 ? '#fafbfc' : 'transparent' }}>
                            {lead.map((c) => <td key={c.key} style={c.center ? td : tdL} title={c.title ? c.title(r) : undefined}>{c.get(r)}</td>)}
                            {dates.map((d) => <td key={d} style={td}>{fmtH(r.byDate[d])}</td>)}
                            <td style={{ ...td, fontWeight: 700 }}>{fmtH(r.total)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        {lead.map((c, i) => <td key={c.key} style={{ ...ft, textAlign: i === 0 ? 'left' : 'center' }}>{i === 0 ? 'Total Hours' : (c.foot ? c.foot(rows) : '')}</td>)}
                        {dates.map((d) => <td key={d} style={ft}>{fmtH(rows.reduce((a, r) => a + (r.byDate[d] || 0), 0))}</td>)}
                        <td style={ft}>{fmtH(foot)}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

// Paid rate vs target: one row per rate sheet class, job to date. The
// delta reads red when the paid wage runs over the sheet's target (every
// dollar over bills at 1.633× but was never in the estimate), green under.
function LaborRateTable({ palette, rows }) {
    const INK = '#1e293b';
    const th = { background: INK, color: '#fff', fontSize: 7.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' };
    const thL = { ...th, textAlign: 'left' };
    const td = { fontSize: 11, color: palette.text, padding: '5px 8px', borderBottom: `1px solid ${palette.border}`, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
    const tdL = { ...td, textAlign: 'left', whiteSpace: 'normal' };
    const ft = { ...td, fontWeight: 700, borderTop: `1px solid ${INK}`, borderBottom: 'none', background: '#f8fafc' };
    const tone = (d) => (d == null ? palette.muted : d > 0.005 ? '#b91c1c' : d < -0.005 ? '#15803d' : palette.text);
    const hours = rows.reduce((a, r) => a + r.hours, 0);
    const cost = rows.reduce((a, r) => a + r.cost, 0);
    const paid = hours > 0 ? cost / hours : 0;
    // Target and bill-rate feet weight only the classes the sheet prices.
    const bp = rows.filter((r) => r.target > 0);
    const tHours = bp.reduce((a, r) => a + r.hours, 0);
    const wavg = (get) => (tHours > 0 ? bp.reduce((a, r) => a + get(r) * r.hours, 0) / tHours : 0);
    const target = wavg((r) => r.target);
    const paidMapped = wavg((r) => r.paid);
    const delta = paidMapped && target ? paidMapped - target : null;
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                    <tr>
                        <th style={thL}>Classification</th>
                        <th style={th}>People</th>
                        <th style={th}>ST Hours</th>
                        <th style={th}>Paid Rate</th>
                        <th style={th}>Target Base</th>
                        <th style={th}>Δ / hr</th>
                        <th style={th}>Δ %</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.key} style={{ background: i % 2 ? '#fafbfc' : 'transparent' }}>
                            {/* Billable (rate sheet) classification only; the internal JDE titles stay in the tooltip (Steve 10/8). */}
                            <td style={{ ...tdL, color: r.sheet ? palette.text : '#b45309' }} title={r.sheet ? `JDE classification${r.jdes.length === 1 ? '' : 's'}: ${r.label}` : `not mapped to the rate sheet (JDE: ${r.label})`}>{r.sheet || 'Unmapped'}</td>
                            <td style={td}>{r.people}</td>
                            <td style={td}>{fmtH(r.hours)}</td>
                            <td style={{ ...td, fontWeight: 600 }}>{fmtR(r.paid)}</td>
                            <td style={td}>{r.target ? fmtR(r.target) : '—'}</td>
                            <td style={{ ...td, color: tone(r.delta), fontWeight: 600 }}>{fmtS(r.delta)}</td>
                            <td style={{ ...td, color: tone(r.delta) }}>{fmtP(r.pct)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td style={{ ...ft, textAlign: 'left' }}>Weighted by ST hours{bp.length < rows.length ? <span style={{ fontWeight: 400, color: palette.muted }}> (mapped classes only)</span> : ''}</td>
                        <td style={ft}>{rows.reduce((a, r) => a + r.people, 0)}</td>
                        <td style={ft}>{fmtH(hours)}</td>
                        <td style={ft}>{fmtR(paid)}</td>
                        <td style={ft}>{target ? fmtR(target) : '—'}</td>
                        <td style={{ ...ft, color: tone(delta) }}>{fmtS(delta)}</td>
                        <td style={{ ...ft, color: tone(delta) }}>{fmtP(delta != null ? delta / target : null)}</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

// Head count and hours off JDE: the week before the version's week, then
// the version's week, which names the change on the week before it. `short`
// marks a week JDE has not posted labor through yet.
function WeekTiles({ palette, weeks, through }) {
    const num = { fontSize: 24, fontWeight: 600, color: palette.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 };
    const unit = { fontSize: 12, color: palette.muted, marginLeft: 4 };
    const note = { fontSize: 11, color: palette.muted, marginTop: 2, fontVariantNumeric: 'tabular-nums' };
    const signed = (v, f) => (v ? `${v > 0 ? '+' : '−'}${f(Math.abs(v))}` : 'no change');
    const short = (w) => !through || through < w.end;
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
            {weeks.map((w, i) => {
                const prev = i ? weeks[i - 1] : null;
                return (
                    <div key={w.start} style={{ border: `1px solid ${palette.border}`, borderRadius: 10, background: '#f8fafc', padding: '10px 14px' }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: palette.muted }}>
                            {i ? 'Report week' : 'Week before'} · {fmtD(w.start)} – {fmtD(w.end)}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '0 20px', marginTop: 2 }}>
                            <span><span style={num}>{w.people.toLocaleString()}</span><span style={unit}>people</span></span>
                            <span><span style={num}>{fmtH(w.hours) || 0}</span><span style={unit}>hours</span></span>
                        </div>
                        {short(w) ? <div style={note}>JDE has labor through {through ? fmtD(through) : 'no date yet'}</div>
                            : prev && !short(prev) ? <div style={note}>{signed(w.people - prev.people, (v) => v.toLocaleString())} people, {signed(w.hours - prev.hours, fmtH)} hours on the week before</div>
                            : null}
                    </div>
                );
            })}
        </div>
    );
}

function Block({ palette, title, right, children }) {
    return (
        <div style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'inline-block', width: 3, height: 12, background: palette.blue600 }} />
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: palette.text }}>{title}</span>
                </div>
                <span style={{ fontSize: 10, color: palette.muted }}>{right}</span>
            </div>
            {children}
        </div>
    );
}

function VoltaGridEquipment({ palette, billRev, versionStart }) {
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    const [selectedJob] = useAtom(selectedJobAtom);
    const ratesReady = !!billRev && billRev.ready;
    // Equipment rolls up by rate sheet item, as on the field tickets (Seth
    // 10/9) - except where the unit's own description names the machine and
    // its JDE tier is another one's, or has no rate (billingEquipNames
    // MACHINE_RULES, Seth 10/29).
    // A time card name that is already a rate sheet item ("Man lift - 135' Lift")
    // is project controls' call and wins over the unit's JDE tier too.
    const byTier = (ratesReady && billRev.equipName) || null;
    const sheetRows = (ratesReady && billRev.equipRows) || null;
    const nameUnit = useMemo(() => (byTier ? (unit, desc) => {
        const m = machineName(desc);
        if (m) return { name: m, via: 'machine' };
        const k = normName(desc);
        const row = k && (sheetRows || []).find((r) => normName(r.name) === k);
        // The job's Service Order swap still applies (PB4 bills the UTV row as "ATV - 4 Wheel").
        const swap = row && equipRowAs(selectedJob)[row.name];
        const to = swap && (sheetRows || []).find((r) => normName(r.name) === normName(swap));
        return row ? { name: (to || row).name, via: 'sheet' } : byTier(unit, desc);
    } : null), [byTier, sheetRows, selectedJob]);
    const { loading, labor } = useJobLabor({ versionStart });
    // Equipment comes off the week's reviewed time card workbook (Seth 10/29:
    // "the equipment should likely come from the timecard excel for the week").
    const versionWe = versionWeeks(versionStart)[1].end;
    const card = useWeekCard(selectedJob, versionWe);
    const equip = useMemo(() => cardEquipment(card.equipment, card.we || versionWe, nameUnit), [card.equipment, card.we, versionWe, nameUnit]);
    // Rate sheet classes only: the Unmapped row (JDE classifications with no
    // class on the Billing → Rates & Rules map, so no target wage) stays off
    // this client-facing table (Seth 10/9).
    const rateRows = useMemo(() => (ratesReady ? laborRatesOf(jobCostDetail, billRev.billsAs, billRev.rateCfg, billRev.personCls, billRev.sheetLabor).filter((r) => r.sheet) : []), [ratesReady, jobCostDetail, billRev]);
    const equipHours = equip.units.reduce((a, u) => a + u.total, 0);
    const laborHours = labor.classes.reduce((a, c) => a + c.total, 0);
    const week = labor.weeks[labor.weeks.length - 1];
    const cardRight = card.loading ? 'loading the time card…'
        : card.error ? 'time card unavailable'
        : card.we ? `${card.file && /week_summary/i.test(card.file.name || '') ? 'Week summary' : 'Time card'} WE ${fmtD(card.we)} · ${equip.units.length} units — ${fmtH(equipHours) || 0} hours`
        : '';

    return (
        <div className="vg-equipment-section" style={{ background: palette.card, borderRadius: 10, border: `1px solid ${palette.border}`, boxShadow: '0 4px 16px rgba(15,23,42,.06)', padding: 16, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: 11, fontWeight: 700, color: palette.text, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>
                    Crew &amp; Equipment — Week Totals
                </h3>
            </div>
            {loading ? (
                <div style={{ fontSize: 11, color: palette.muted }}>Loading crew and equipment…</div>
            ) : (
                <>
                    <Block palette={palette} title="Labor — by classification (JDE)"
                        right={week ? `Week of ${fmtD(week.start)} – ${fmtD(week.end)} · ${labor.people} people — ${fmtH(laborHours) || 0} hours` : ''}>
                        {labor.behind && (
                            <div style={{ fontSize: 11, color: palette.muted, marginBottom: 8 }}>
                                The version's week ({fmtD(labor.behind.start)} – {fmtD(labor.behind.end)}) has not posted in JDE yet — showing the newest week JDE has.
                            </div>
                        )}
                        <WeekTiles palette={palette} weeks={labor.weeks} through={labor.through} />
                        {labor.classes.length ? (
                            <WeekTable palette={palette} dates={labor.dates} rows={labor.classes} foot={laborHours}
                                lead={[
                                    { key: 'cls', label: 'Classification', get: (r) => r.label },
                                    { key: 'people', label: 'People', center: true, get: (r) => r.people, foot: () => labor.people },
                                ]} />
                        ) : <div style={{ fontSize: 11, color: palette.muted }}>No JDE labor hours have posted for this week.</div>}
                    </Block>
                    <Block palette={palette} title="Equipment — by type (time card)" right={cardRight}>
                        {card.we && card.we !== versionWe && (
                            <div style={{ fontSize: 11, color: palette.muted, marginBottom: 8 }}>
                                No time card or week summary workbook for the version's week (WE {fmtD(versionWe)}) in SharePoint — showing the newest one before it.
                            </div>
                        )}
                        {card.loading ? <div style={{ fontSize: 11, color: palette.muted }}>Loading the time card…</div>
                            : equip.types.length ? (
                                <WeekTable palette={palette} dates={equip.dates} rows={equip.types} foot={equipHours}
                                    lead={[
                                        { key: 'type', label: 'Equipment', get: (r) => r.label },
                                        { key: 'units', label: 'Units', center: true, get: (r) => r.units, foot: (rows) => rows.reduce((a, r) => a + r.units, 0) },
                                    ]} />
                            ) : <div style={{ fontSize: 11, color: palette.muted }}>
                                {card.error ? `Could not read the time card: ${card.error}` : `No time card workbook with equipment in SharePoint for WE ${fmtD(versionWe)} or the ${CARD_LOOKBACK} weeks before it.`}
                            </div>}
                    </Block>
                    <Block palette={palette} title="Craft Labor — paid rate vs target base wage (job to date)"
                        right={!ratesReady ? (billRev && billRev.error ? 'rate sheet unavailable' : 'loading rate sheet…') : ''}>
                        {ratesReady && rateRows.length ? (
                            <LaborRateTable palette={palette} rows={rateRows} />
                        ) : ratesReady ? <div style={{ fontSize: 11, color: palette.muted }}>No straight-time labor cost has posted.</div> : null}
                    </Block>
                </>
            )}
        </div>
    );
}

export default VoltaGridEquipment;
